import { useState } from "react";
import { ChevronLeft, ChevronRight, ImageOff, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

export interface PILPhoto {
  id: string;
  signedUrl: string;
}

interface PILPhotosStripProps {
  photos: PILPhoto[];
}

export function PILPhotosStrip({ photos }: PILPhotosStripProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (photos.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-6 text-xs text-muted-foreground">
        <ImageOff className="h-4 w-4" />
        No photos for this PIL.
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-2 overflow-x-auto scrollbar-none px-3 py-2">
        {photos.map((photo, i) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setLightboxIndex(i)}
            className="shrink-0 h-24 w-24 rounded-md overflow-hidden border bg-muted hover:ring-2 hover:ring-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Open photo ${i + 1}`}
          >
            <img src={photo.signedUrl} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>

      <Dialog
        open={lightboxIndex !== null}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
      >
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-0" showCloseButton={false}>
          <DialogTitle className="sr-only">PIL photo</DialogTitle>
          <DialogDescription className="sr-only">
            Full-size PIL photo preview
          </DialogDescription>
          {lightboxIndex !== null && photos[lightboxIndex] && (
            <div className="relative flex items-center justify-center h-[80vh]">
              <img
                src={photos[lightboxIndex].signedUrl}
                alt=""
                className="max-h-full max-w-full object-contain"
              />
              <button
                type="button"
                onClick={() => setLightboxIndex(null)}
                className="absolute top-3 right-3 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length)
                    }
                    className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
                    aria-label="Previous photo"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex((lightboxIndex + 1) % photos.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
                    aria-label="Next photo"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                    {lightboxIndex + 1} / {photos.length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
